import { Alert, Box, Button, Chip, Grid, Link, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { ToastContent } from "../../../shared/components";
import { useToast } from "../../../shared/hooks";
import { SetupChargeService } from "../../../shared/services/api/setupCharge/SetupChargeService";
import { ISetupCharge, ISetupChargeQueueProblems, ISetupChargeQueueRequest, ISetupChargeRequestListParams } from "../../../shared/services/api/setupCharge/Types";
import { setupChargeFormatCurrency, setupChargeFormatDate } from "../utils/setupChargeFormat";
import { setupChargeRequestStateColor, setupChargeRequestStateLabel, setupChargeRequestTypeLabel } from "../utils/setupChargeStatus";

interface SetupChargeRequestsPanelProps {
	canManage: boolean;
	/** Abre o detalhe da cobrança ligada à solicitação. */
	onOpenCharge: (chargeId: string) => void;
}

interface IRequestFilters {
	type: string;
	state: string;
	dateStart: string;
	dateEnd: string;
	search: string;
}

const EMPTY_FILTERS: IRequestFilters = { type: "", state: "", dateStart: "", dateEnd: "", search: "" };

/** Solicitação ainda em andamento depois deste tempo é tratada como atrasada (a meta de processamento é de poucos minutos). */
const STALE_AFTER_MINUTES = 10;
const PROBLEM_LIST_SIZE = 50;

const minutesSince = (value?: string | null): number => {
	const time = value ? new Date(value).getTime() : NaN;
	return Number.isFinite(time) ? Math.floor((Date.now() - time) / 60000) : 0;
};

const STATE_OPTIONS = [
	{ value: "", label: "Todos os resultados" },
	{ value: "rejected", label: "Recusadas" },
	{ value: "received,processing", label: "Em andamento" },
	{ value: "dead", label: "Com falha (dead)" },
	{ value: "completed", label: "Concluídas" },
];

const TYPE_OPTIONS = [
	{ value: "", label: "Todos os tipos" },
	{ value: "CREATE", label: "Criação" },
	{ value: "CANCEL", label: "Cancelamento" },
	{ value: "RESEND", label: "Reenvio do link" },
	{ value: "CONTACT_UPDATE", label: "Correção de contato" },
];

export const SetupChargeRequestsPanel = ({ canManage, onOpenCharge }: SetupChargeRequestsPanelProps) => {
	const toastHandler = useToast();
	const [filters, setFilters] = useState<IRequestFilters>(EMPTY_FILTERS);
	const [applied, setApplied] = useState<IRequestFilters>(EMPTY_FILTERS);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [rows, setRows] = useState<ISetupChargeQueueRequest[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [problems, setProblems] = useState<ISetupChargeQueueProblems>({ requests: [], deliveries: [] });
	const [problemsError, setProblemsError] = useState<string | null>(null);
	const [retryingId, setRetryingId] = useState<string | null>(null);
	const [failedCharges, setFailedCharges] = useState<ISetupCharge[]>([]);
	const [staleRequests, setStaleRequests] = useState<ISetupChargeQueueRequest[]>([]);

	const showToast = (type: "success" | "error", content: string) =>
		toastHandler.present({ type, position: "top-right", messageOrContent: <ToastContent title="Taxa Setup Avulsa" content={content} /> });

	const loadRequests = useCallback(async (current: IRequestFilters, currentPage: number, limit: number) => {
		const params: ISetupChargeRequestListParams = { ...current, page: currentPage + 1, pageSize: limit };
		const result = await SetupChargeService.listRequests(params);
		if (result instanceof Error) {
			setRows([]);
			setTotalCount(0);
			setErrorMessage(result.message);
			return;
		}
		setErrorMessage(null);
		setRows(result.data);
		setTotalCount(result.totalCount);
	}, []);

	const loadProblems = useCallback(async () => {
		const result = await SetupChargeService.getQueueProblems();
		if (result instanceof Error) {
			setProblemsError(result.message);
			return;
		}
		setProblemsError(null);
		setProblems(result);

		// além de `dead`: cobranças em `failed` (reprocessáveis pelo detalhe) e solicitações paradas além da meta
		const [failed, running] = await Promise.all([
			SetupChargeService.list({ status: "failed", page: 1, pageSize: PROBLEM_LIST_SIZE }),
			SetupChargeService.listRequests({ state: "received,processing", page: 1, pageSize: PROBLEM_LIST_SIZE }),
		]);
		setFailedCharges(failed instanceof Error ? [] : failed.data ?? []);
		setStaleRequests(running instanceof Error ? [] : (running.data ?? []).filter((request) => minutesSince(request.createdAt) >= STALE_AFTER_MINUTES));
	}, []);

	useEffect(() => {
		loadRequests(EMPTY_FILTERS, 0, rowsPerPage);
		loadProblems();
	}, []);

	const handleRetryRequest = async (requestId: string) => {
		setRetryingId(requestId);
		const result = await SetupChargeService.retryRequest(requestId);
		setRetryingId(null);
		if (result instanceof Error) {
			showToast("error", result.message);
			return;
		}
		showToast("success", "Solicitação devolvida à fila para ser processada de novo.");
		loadProblems();
		loadRequests(applied, page, rowsPerPage);
	};

	const handleRetryDelivery = async (deliveryId: string) => {
		setRetryingId(deliveryId);
		const result = await SetupChargeService.retryDelivery(deliveryId);
		setRetryingId(null);
		if (result instanceof Error) {
			showToast("error", result.message);
			return;
		}
		showToast("success", "Entrega devolvida à fila para ser enviada de novo.");
		loadProblems();
	};

	const hasProblems = problems.requests.length > 0 || problems.deliveries.length > 0 || failedCharges.length > 0 || staleRequests.length > 0;

	return (
		<Box>
			<Paper variant="outlined" sx={{ mt: 1, mx: 1, p: 2, width: "96%", borderRadius: 2 }}>
				<Typography variant="subtitle1" fontWeight="bold" mb={1}>
					Itens da fila com problema
				</Typography>
				{problemsError && <Alert severity="error">{problemsError}</Alert>}
				{!problemsError && !hasProblems && (
					<Typography variant="body2" color="textSecondary">
						Nenhuma cobrança com falha, solicitação parada ou entrega de webhook esgotou as tentativas.
					</Typography>
				)}
				{failedCharges.map((charge) => (
					<Box key={charge.id} display="flex" alignItems="center" gap={1} flexWrap="wrap" py={0.5}>
						<Chip size="small" color="error" label="Cobrança com falha" />
						<Typography variant="body2">
							{charge.payerName ?? ""} · {charge.clientName ?? ""} · {setupChargeFormatCurrency(charge.amount)} · {setupChargeFormatDate(charge.createdAt, true)}
						</Typography>
						<Button size="small" variant="outlined" onClick={() => onOpenCharge(charge.id)}>
							Abrir cobrança
						</Button>
					</Box>
				))}
				{staleRequests.map((request) => (
					<Box key={request.requestId} display="flex" alignItems="center" gap={1} flexWrap="wrap" py={0.5}>
						<Chip size="small" color="warning" label="Atrasada" />
						<Typography variant="body2">
							{setupChargeRequestTypeLabel(request.type)} · {request.partner === "panel" ? "Painel" : request.partner} · {request.clientName ?? ""} · em andamento há {minutesSince(request.createdAt)} min
						</Typography>
						{request.chargeId && (
							<Button size="small" variant="outlined" onClick={() => onOpenCharge(request.chargeId as string)}>
								Abrir cobrança
							</Button>
						)}
					</Box>
				))}
				{problems.requests.map((request) => (
					<Box key={request.requestId} display="flex" alignItems="center" gap={1} flexWrap="wrap" py={0.5}>
						<Chip size="small" color="error" label="Solicitação" />
						<Typography variant="body2">
							{setupChargeRequestTypeLabel(request.type)} · {request.partner} · {request.clientName ?? ""} · {request.attempts ?? 0}/{request.maxAttempts ?? 0} tentativas
						</Typography>
						<Typography variant="body2" color="error">
							{request.lastError ?? request.resultMessage ?? ""}
						</Typography>
						{canManage && (
							<Button size="small" variant="outlined" disabled={retryingId === request.requestId} onClick={() => handleRetryRequest(request.requestId)}>
								Reprocessar
							</Button>
						)}
					</Box>
				))}
				{problems.deliveries.map((delivery) => (
					<Box key={delivery.id} display="flex" alignItems="center" gap={1} flexWrap="wrap" py={0.5}>
						<Chip size="small" color="error" label="Webhook" />
						<Typography variant="body2">
							{delivery.eventType} · {delivery.partner} · {delivery.attempts ?? 0} tentativas
						</Typography>
						<Typography variant="body2" color="error">
							{delivery.lastError ?? ""}
						</Typography>
						{canManage && (
							<Button size="small" variant="outlined" disabled={retryingId === delivery.id} onClick={() => handleRetryDelivery(delivery.id)}>
								Reenviar
							</Button>
						)}
					</Box>
				))}
			</Paper>

			<Paper variant="outlined" sx={{ mt: 1, mx: 1, p: 2, width: "96%", borderRadius: 2 }}>
				<form
					onSubmit={(event) => {
						event.preventDefault();
						setApplied(filters);
						setPage(0);
						loadRequests(filters, 0, rowsPerPage);
					}}
				>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} sm={6} md={3}>
							<TextField select fullWidth size="small" label="Tipo" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
								{TYPE_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</TextField>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<TextField select fullWidth size="small" label="Resultado" value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })}>
								{STATE_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</TextField>
						</Grid>
						<Grid item xs={6} sm={3} md={2}>
							<TextField fullWidth size="small" type="date" label="De" InputLabelProps={{ shrink: true }} value={filters.dateStart} onChange={(e) => setFilters({ ...filters, dateStart: e.target.value })} />
						</Grid>
						<Grid item xs={6} sm={3} md={2}>
							<TextField fullWidth size="small" type="date" label="Até (inclusive)" InputLabelProps={{ shrink: true }} value={filters.dateEnd} onChange={(e) => setFilters({ ...filters, dateEnd: e.target.value })} />
						</Grid>
						<Grid item xs={12} md={4}>
							<TextField fullWidth size="small" label="Código do parceiro ou da solicitação" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
						</Grid>
						<Grid item xs={12} md="auto">
							<Box display="flex" gap={1}>
								<Button type="submit" variant="contained" disableElevation>
									Filtrar
								</Button>
								<Button
									variant="outlined"
									onClick={() => {
										setFilters(EMPTY_FILTERS);
										setApplied(EMPTY_FILTERS);
										setPage(0);
										loadRequests(EMPTY_FILTERS, 0, rowsPerPage);
									}}
								>
									Limpar
								</Button>
							</Box>
						</Grid>
					</Grid>
				</form>
			</Paper>

			{errorMessage && (
				<Box mt={1} mx={1}>
					<Alert severity="error">{errorMessage}</Alert>
				</Box>
			)}

			<TableContainer component={Paper} sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "auto", maxHeight: "60vh" }}>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow>
							{["Data", "Tipo", "Parceiro", "Imobiliária", "Código do parceiro", "Resultado", "Motivo"].map((label) => (
								<TableCell key={label} sx={{ backgroundColor: "grey.100" }}>
									<Typography variant="button">{label}</Typography>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.length === 0 && (
							<TableRow>
								<TableCell colSpan={7}>
									<Typography variant="body2" color="text.secondary" align="center">
										Nenhuma solicitação encontrada.
									</Typography>
								</TableCell>
							</TableRow>
						)}
						{rows.map((request) => (
							<TableRow key={request.requestId} hover>
								<TableCell>{setupChargeFormatDate(request.createdAt, true)}</TableCell>
								<TableCell>{setupChargeRequestTypeLabel(request.type)}</TableCell>
								<TableCell>{request.partner === "panel" ? "Painel" : request.partner}</TableCell>
								<TableCell>{request.clientName ?? ""}</TableCell>
								<TableCell>
									{request.chargeId ? (
										<Link component="button" variant="body2" onClick={() => onOpenCharge(request.chargeId as string)}>
											{request.externalId ?? "Ver cobrança"}
										</Link>
									) : (
										request.externalId ?? ""
									)}
								</TableCell>
								<TableCell>
									<Chip size="small" label={setupChargeRequestStateLabel(request.state)} color={setupChargeRequestStateColor(request.state)} />
								</TableCell>
								<TableCell>
									<Typography variant="body2" color={request.state === "completed" ? "textSecondary" : "error"}>
										{[request.resultCode, request.resultMessage].filter(Boolean).join(": ")}
									</Typography>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
				<TablePagination
					component="div"
					count={totalCount}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={(_, next) => {
						setPage(next);
						loadRequests(applied, next, rowsPerPage);
					}}
					onRowsPerPageChange={(e) => {
						const limit = parseInt(e.target.value, 10);
						setRowsPerPage(limit);
						setPage(0);
						loadRequests(applied, 0, limit);
					}}
					rowsPerPageOptions={[10, 25, 50]}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</TableContainer>
		</Box>
	);
};
